import { CustomPreloader } from '@components'
import {
  useBanksRequest,
  useCancelPaymentRequest,
  useEventTracker,
  useFilteredBanks,
  usePaymentOptions
} from 'mmfintech-checkout-commons'
import type { PaymentOptionResponse, SupportedBank } from 'mmfintech-checkout-commons/types'
import { isSingleItemArray, isValidArray, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useEffect, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import type { VoucherProps } from '../../../@types'
import { VoucherSteps } from '../../../constants'
import { useVoucherFlow } from '../../../hooks'
import { BlixcardBanksStep } from './BlixcardBanksStep'
import { BlixcardFieldsStep } from './BlixcardFieldsStep'
import { BlixcardMethodStep } from './BlixcardMethodStep'
import { BlixcardOptionsStep } from './BlixcardOptionsStep'
import { BlixcardRedeemStep } from './BlixcardRedeemStep'

export const BlixcardVoucher = (props: VoucherProps) => {
  const { sessionId, fieldValues, countryCode, setCountryCode, paymentMethod, setPaymentMethod, handlePay } = props

  const { checkoutPayError, checkoutPayFetching, session, sessionError } = useSelector(
    ({ checkout: { checkoutPayError, checkoutPayFetching, session, sessionError } }) => ({
      checkoutPayError,
      checkoutPayFetching,
      session,
      sessionError
    }),
    shallowEqual
  )

  const history = useHistory()

  const { trackEvent } = useEventTracker()
  const { cancelPayment } = useCancelPaymentRequest()

  const { paymentOptions } = usePaymentOptions()
  const { banks, banksCount, banksError } = useBanksRequest()

  const hasPreselectedBank = useMemo(
    () => Array.isArray(banks) && banks.some(entry => Boolean(entry?.preselect)),
    [banks]
  )

  const voucherFlow = useVoucherFlow({
    handlePay,
    hasAdditionalFields: fieldValues.hasAdditionalFields,
    hasBankSelection: fieldValues.hasBankSelection,
    hasPreselectedBank,
    paymentOptions,
    paymentMethod,
    countryCode
  })

  const filteredBanks = useFilteredBanks((bank: SupportedBank) => handleSelectBank(bank ? bank.bankChoiceId : ''))

  const { showCancelButton, skipVoucherOptionSelection } = session || {}

  const handleCancel = () => {
    if (sessionId) {
      cancelPayment(sessionId)
    }
    void trackEvent(sessionId, 'cancelled_by_payer')
    history.push('/fail')
    return false
  }

  const handleSelectBank = (bankCode: string) => {
    fieldValues.setValue('bankChoiceId', bankCode)
    void trackEvent(sessionId, 'bank_selected', bankCode)
  }

  const handleCountryChanged = (value: string): void => {
    if (value !== countryCode) {
      filteredBanks.reset()
      voucherFlow.resetMethods()
      setCountryCode(value)
    }
  }

  const setMethodInternal = (method: string): void => {
    const found =
      isValidArray(paymentOptions) &&
      paymentOptions.some((option: PaymentOptionResponse) => option.paymentMethod === method)
    setPaymentMethod(found ? method : '')
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on unmount
  useEffect(() => {
    return () => {
      voucherFlow.reset()
    }
  }, [])

  useEffect(() => {
    switch (voucherFlow.option) {
      case 'buy': {
        let method = null
        if (paymentMethod === 'BLIXCARD') {
          method = ''
        }
        if (isSingleItemArray(voucherFlow.filteredMethods)) {
          method = voucherFlow.filteredMethods[0].paymentMethod
        }
        if (paymentMethod !== method && method != null) {
          setMethodInternal(method)
        }
        break
      }

      case 'redeem':
        if (paymentMethod !== 'BLIXCARD') {
          setMethodInternal('BLIXCARD')
        }
        break

      default:
      // nothing
    }
    // eslint-disable-next-line
  }, [voucherFlow.option, countryCode, voucherFlow.filteredMethods])

  useEffect(() => {
    fieldValues.handleErrors(checkoutPayError, {
      'cardDetails.cardNumber': 'cardNumber',
      'cardDetails.expiryMonth': 'expiryMonth',
      'cardDetails.expiryYear': 'expiryYear',
      'cardDetails.cvv': 'cvv'
    })
  }, [checkoutPayError])

  useEffect(() => {
    if (
      voucherFlow.option === 'buy' &&
      paymentMethod &&
      fieldValues.getValue('bankChoiceId') &&
      banksCount === 1 &&
      !checkoutPayFetching
    ) {
      voucherFlow.continueWithPayment()
    }
  }, [fieldValues.getValue('bankChoiceId')])

  return voucherFlow.step === VoucherSteps.NONE ? (
    <CustomPreloader />
  ) : (
    <>
      {voucherFlow.step === VoucherSteps.OPTIONS ? (
        <BlixcardOptionsStep
          option={voucherFlow.option}
          setOption={voucherFlow.setOption}
          handleNext={voucherFlow.handleNext}
          sessionId={sessionId}
        />
      ) : voucherFlow.step === VoucherSteps.METHODS ? (
        <BlixcardMethodStep
          countryCode={countryCode}
          setCountryCode={handleCountryChanged}
          hasCountrySelected={voucherFlow.hasCountrySelected}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          filteredMethods={voucherFlow.filteredMethods}
          handleBack={voucherFlow.hasBack('methods') ? voucherFlow.handleBack : null}
          handleNext={voucherFlow.handleNext}
          skipVoucherOptionSelection={skipVoucherOptionSelection}
        />
      ) : voucherFlow.step === VoucherSteps.FIELDS ? (
        <BlixcardFieldsStep
          countryCode={countryCode}
          paymentMethod={paymentMethod}
          fieldValues={fieldValues}
          handleBack={voucherFlow.hasBack('fields') ? voucherFlow.handleBack : null}
          handleNext={voucherFlow.handleNext}
        />
      ) : voucherFlow.step === VoucherSteps.BANKS ? (
        <BlixcardBanksStep
          countryCode={countryCode}
          paymentMethod={paymentMethod}
          filteredBanks={filteredBanks}
          fieldValues={fieldValues}
          handleBack={voucherFlow.hasBack('banks') ? voucherFlow.handleBack : null}
          handleNext={voucherFlow.handleNext}
          sessionId={sessionId}
        />
      ) : voucherFlow.step === VoucherSteps.REDEEM ? (
        <BlixcardRedeemStep
          paymentMethod={paymentMethod}
          countryCode={countryCode}
          setCountryCode={handleCountryChanged}
          fieldValues={fieldValues}
          hideBack={voucherFlow.hasOnlyBlixcard}
          handleBack={voucherFlow.handleBack}
          handleNext={voucherFlow.handleNext}
        />
      ) : (
        <ErrorDisplay error={[sessionError, checkoutPayError, banksError]} />
      )}

      {showCancelButton ? (
        <div className='buttons-container'>
          <button type='button' className='cancel-btn' onClick={handleCancel} data-test='cancel-button'>
            {tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
          </button>
        </div>
      ) : null}
    </>
  )
}
